import * as React from 'react';
import SidePanel from './SidePanel';
import TrackingDataProvider, { DataPoint } from '../providers/TrackingDataProvider';
import withStyles, { StyleRulesCallback, WithStyles } from '@material-ui/core/styles/withStyles';
import { DatePicker } from 'material-ui-pickers';
import { DocumentCard, TrackingChart } from '../components';
import { FieldProvider, LayoutTools, LayoutToolsProvider } from '../providers';
import { floatToString, timestampToString } from '../utils';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

import {
  Paper,
  Typography,
  // List,
  // ListItem,
  Button,
  Snackbar,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  IconButton
} from '@material-ui/core';

import BackIcon from '@material-ui/icons/ArrowBack';

interface Renders {
  layout: LayoutTools;
  loading: boolean;
  field: Field | null;
  farm: Farm | null;
  points: DataPoint[] | null;
}

interface State {
  result: any | null;
  capturedSince: string;
  capturedUntil: string;
}

const styles: StyleRulesCallback = theme => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    height: '100vh'
  },
  panelItem: {
    marginBottom: theme.spacing.unit * 2
  },
  content: {
    padding: theme.spacing.unit * 3,
    backgroundColor: theme.palette.grey['100'],
    flex: 1,
    overflowY: 'scroll',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  card: {
    width: '100%',
    maxWidth: 900,
    overflow: 'visible',
    marginBottom: theme.spacing.unit * 3
  }
});

type Props = WithStyles & RouteComponentProps<any>;

class AutoTracking extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      result: null,
      capturedSince: '2017-05-01',
      capturedUntil: '2018-05-01'
    };
  }

  renderLoading(message?: string | null) {
    return (
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={true}
        message={
          <>
            <CircularProgress color="secondary" size={25} />
            <span>{message || 'cargando'}</span>
          </>
        }
      />
    );
  }

  renderBigLoading() {
    const { classes } = this.props;
    return (
      <Paper square={true} elevation={1} className={classes.content}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <CircularProgress size={150} />
          <Typography variant="title" color="textSecondary">
            Ejecutando Agro-ritmo
          </Typography>
        </div>
      </Paper>
    );
  }

  renderOutputChart(datapoints: DataPoint[]) {
    const { classes } = this.props;
    return (
      <>
        <Card elevation={1} className={classes.card}>
          <CardHeader title="Evolución NDVI" />
          <CardContent>
            <TrackingChart width="100%" height={300} data={datapoints} />
          </CardContent>
        </Card>
        <Card elevation={1} className={classes.card}>
          <CardHeader title="Tabla de Datos" />
          <CardContent>
            <Table>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell>Mes</TableCell>
                  <TableCell numeric={true}>Total Lote</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {datapoints.map(r => {
                  return (
                    <TableRow key={r.id} hover={true}>
                      <TableCell>{timestampToString(r.date)}</TableCell>
                      <TableCell numeric={true}>
                        {floatToString(r.meanNdvi, 2)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        <Grid container={true} spacing={24} style={{ maxWidth: 900 }}>
          {datapoints.map(doc => (
            <Grid key={doc.id} item={true} xs={12} sm={4}>
              <DocumentCard
                meanNdvi={doc.meanNdvi}
                date={timestampToString(doc.date)}
                thumbnailUrl={doc.thumbnailUrl}
                selectable={false}
              />
            </Grid>
          ))}
        </Grid>
      </>
    );
  }

  composeProviders(children: (r: Renders) => React.ReactNode) {
    const {
      match: {
        params: { fieldId }
      }
    } = this.props;

    return (
      <LayoutToolsProvider>
        {layout => (
          <FieldProvider
            fieldId={fieldId}
            includeFarm={true}
            includeLayers={false}
          >
            {fieldRenders => (
              <TrackingDataProvider
                fieldId={fieldId}
                capturedSince={this.state.capturedSince}
                capturedUntil={this.state.capturedUntil}
              >
                {trackingRenders =>
                  children({
                    layout,
                    loading: fieldRenders.loading,
                    field: fieldRenders.field,
                    farm: fieldRenders.farm,
                    points: trackingRenders.points || null
                  })
                }
              </TrackingDataProvider>
            )}
          </FieldProvider>
        )}
      </LayoutToolsProvider>
    );
  }

  renderPanelItems(renders: Renders) {
    const { classes } = this.props;
    return (
      <>
        <div style={{display: 'flex'}}>
          <div style={{flex: 0, marginRight: 16, alignItems: 'center'}}>
            <IconButton onClick={() => this.props.history.goBack()}>
              <BackIcon />
            </IconButton>
          </div>
          <div style={{flex: 1}}>
            <Typography variant="subheading">Seguimiento Automatico</Typography>
            <Typography variant="headline" className={classes.panelItem}>
              {(renders.field && renders.field.name) || 'Cargando Lote...'}
            </Typography>
          </div>
        </div>
        <DatePicker
          className={classes.panelItem}
          fullWidth={true}
          label="Comienzo"
          clearable={true}
          disableFuture={true}
          clearLabel="Limpiar"
          value={this.state.capturedSince}
          onChange={d => this.setState({ capturedSince: d })}
          labelFunc={date => (date ? date.format('DD/MM/YYYY') : '')}
        />
        <DatePicker
          className={classes.panelItem}
          fullWidth={true}
          label="Finalización"
          clearable={true}
          disableFuture={true}
          clearLabel="Limpiar"
          value={this.state.capturedUntil}
          onChange={d => this.setState({ capturedUntil: d })}
          labelFunc={date => (date ? date.format('DD/MM/YYYY') : '')}
        />
        <Button className={classes.panelItem} variant="raised" fullWidth={true}>
          Actualizar
        </Button>
        <Button
          className={classes.panelItem}
          color="primary"
          variant="raised"
          fullWidth={true}
        >
          Exportar Reporte
        </Button>
      </>
    );
  }

  render() {
    const { classes } = this.props;

    return this.composeProviders(renders => (
      <main className={classes.root}>
        {renders.loading && this.renderLoading('cargando lote')}
        <SidePanel>
          {this.renderPanelItems(renders)}
        </SidePanel>
        <Paper square={true} elevation={0} className={classes.content}>
          {!renders.loading &&
            renders.points &&
            this.renderOutputChart(renders.points)}
        </Paper>
      </main>
    ));
  }
}

export default withStyles(styles)(withRouter(AutoTracking));



// WEBPACK FOOTER //
// ./src/containers/AutoTracking.tsx